import React, { useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import ImageClusterPreview from './ImageClusterPreview';
import ImageClusterFinder from './ImageClusterFinder';
import { ImagePreview } from './ImagePreview';
import CountryFlag from '../common/CountryFlag';
import useAsyncEffect from '../common/react-hooks/useAsyncEffect';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { IconButton } from '../common/IconButton';
import BadgeColorHash from '../common/BadgeColorHash';
import { clearCacheByUrlAndId } from '../common/react-hooks/useGetCachedDocIdBatched';

export default function ModalMoveImageClusterImages({ sourceCluster, selectedImageIds, onSave, onCancel }) {
  const { page } = useContext(LegoAdminPageContext);

  // `destination` is either an existing cluster doc or { isNew: true, context, clusterType }.
  const [destination, setDestination] = useState(null);
  const [estimate, setEstimate] = useState(null);

  const sourceId = sourceCluster._id;
  const clusterType = sourceCluster.clusterType;
  const modelId = sourceCluster.context?.modelId;

  const selectedIdSet = useMemo(() => new Set(selectedImageIds.map(String)), [selectedImageIds]);
  const selectedImages = useMemo(
    () => _.filter(sourceCluster.images, img => selectedIdSet.has(String(img.id))),
    [sourceCluster, selectedIdSet]
  );

  const opsQuery = useMemo(() => {
    if (!destination) return null;
    const base = { operation: 'moveImages', fromId: sourceId, imageIds: selectedImageIds };
    return destination.isNew
      ? { ...base, newCluster: { context: destination.context } }
      : { ...base, intoId: destination._id };
  }, [destination, sourceId, selectedImageIds]);

  const destModelId = destination?.isNew ? destination.context.modelId : destination?.context?.modelId;
  const crossModel = destination && destModelId && destModelId !== modelId;

  useAsyncEffect(async () => {
    setEstimate(null);
    if (!opsQuery) return;
    try {
      const res = await page.service('/services/data/image-clusters/operations').find({ query: opsQuery });
      setEstimate(res);
    } catch (error) {
      page.alert(error.message);
    }
  }, [JSON.stringify(opsQuery)]);

  const moveImages = () => {
    page.runAsync(async () => {
      await page.service('/services/data/image-clusters/operations').create(opsQuery);
      clearCacheByUrlAndId('services/data/image-clusters', sourceId);
      if (destination._id) clearCacheByUrlAndId('services/data/image-clusters', destination._id);
      onSave();
    }, 'Moving images...');
  };

  if (!destination) {
    return <div className='d-flex' style={{ height: '100%', overflow: 'hidden' }}>
      <div style={{ width: '260px', flexShrink: 0 }} className='border-right bg-light overflow-auto p-2'>
        <div className='bg-light-info p-2 mb-2 small'>
          Moving <strong>{selectedImageIds.length}</strong> images from <BadgeColorHash id={sourceId}/> (model {modelId}).
        </div>
        <div className='d-flex flex-wrap'>
          {selectedImages.map(img => (
            <span key={img.id} className='m-1' title={JSON.stringify(img.vehicle)}>
              <ImagePreview img100 cover url={img.url || ''} className='bg-white' zoom context={img.vehicle}/>
              <div className='small zoom-90 text-center'>
                <CountryFlag countryCode={(img.vehicle?.countryCode || '').toLowerCase()}/>
                <span className='ml-1'>{img.vehicle?.year}</span>
              </div>
            </span>
          ))}
        </div>
      </div>
      
      <div className='flex-grow-1 overflow-auto'>
        <div className='bg-light-info p-2 mb-2'>
          <button className='btn btn-secondary mr-2' onClick={onCancel}>Cancel</button>
          Pick a destination cluster (any model), or create a new one:   
        </div>
        <ImageClusterFinder clusterType={clusterType}
                            vehicleContext={{ modelId }}
                            findSimilarToImages={selectedImageIds}
                            onClusterSelected={(c) => c && c._id !== sourceId && setDestination(c)}
                            onCreateNew={(context) => setDestination({ isNew: true, context, clusterType })}/>
      </div>
    </div>;
  }

  return <div>
    <div className='bg-light-danger p-2'>
      MOVE {selectedImageIds.length} images FROM <BadgeColorHash id={sourceId}/> (model {modelId}):<br/>
      <ImageClusterPreview clusterId={sourceId} maxImages={15} highlightImageIds={selectedImageIds}/>
    </div>
    <div className='bg-light-success p-2'>
      {destination.isNew
        ? <>INTO <strong>new cluster</strong> (model {destination.context.modelId})</>
        : <>INTO <BadgeColorHash id={destination._id}/> (model {destination.context?.modelId})</>}<br/>
      {destination.isNew
        ? <div className='text-muted small'>A fresh cluster will be created with the moved images.</div>
        : <ImageClusterPreview clusterId={destination._id} maxImages={15} preloadedCluster={destination}/>}
    </div>
    {crossModel ? <div className='alert alert-warning p-2 my-2'>
      Cross-model move: images go to model <strong>{destModelId}</strong> (source was <strong>{modelId}</strong>).
      DataIssues will be retargeted to <strong>{destModelId}</strong>; DataFeedback records remain on <strong>{modelId}</strong>.
    </div> : null}
    <IconButton icon='west' level='primary' onClick={() => setDestination(null)}>Pick a different destination</IconButton>

    <div style={{ minHeight: '100px' }} className='bg-light mt-2'>
      {estimate ? <pre>{JSON.stringify(estimate, null, 2)}</pre> : <div>Loading estimate...</div>}
    </div>

    <div className='text-center'>
      <button className='btn btn-primary' onClick={moveImages} disabled={!estimate}>Move</button>
      <button className='btn btn-secondary' onClick={onCancel}>Cancel</button>
    </div>
  </div>;
}
